import { getOperacoes, deleteOperacao } from "@spark/services/operacoes";
import { useState, useEffect, useCallback, useRef } from 'react';
import { notification } from '@spark/ui-components/utils/notification';
export const useOperacoesListagem = (clienteId = null) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 5
  });
  const [filters, setFilters] = useState({
    NomeOperacao: '',
    TipoDaOperacao: '',
    Situacao: '',
    GerarCobranca: ''
  });
  const isMountedRef = useRef(true);
  const didInitialLoadRef = useRef(false);
  const inFlightSignatureRef = useRef(null); // Cleanup quando componente é desmontado

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []); // Função para buscar operações com filtros

  const fetchOperacoes = useCallback(async (filtrosParaBuscar = filters) => {
    if (!isMountedRef.current) return;

    try {
      console.log('🌐 Buscando operações com filtros:', filtrosParaBuscar); // Preparar filtros para o serviço

      const requestFilters = {
        quantidadePorPagina: paginationState.pageSize,
        pagina: paginationState.pageIndex + 1,
        ...(filtrosParaBuscar.NomeOperacao && {
          NomeOperacao: filtrosParaBuscar.NomeOperacao
        }),
        ...(filtrosParaBuscar.TipoDaOperacao && {
          TipoDaOperacao: filtrosParaBuscar.TipoDaOperacao
        }),
        ...(filtrosParaBuscar.Situacao && {
          Situacao: filtrosParaBuscar.Situacao
        }),
        ...(filtrosParaBuscar.GerarCobranca !== '' && {
          GerarCobranca: filtrosParaBuscar.GerarCobranca
        }),
        ...(clienteId && {
          ClienteId: clienteId
        })
      }; // Evitar chamadas duplicadas (StrictMode/dev ou race de effects)

      const signature = JSON.stringify(requestFilters);

      if (inFlightSignatureRef.current === signature) {
        console.log('⏭️ Pulando chamada duplicada (mesma assinatura)');
        return;
      }

      inFlightSignatureRef.current = signature;
      setLoading(true);
      const response = await getOperacoes(requestFilters);
      if (!isMountedRef.current) return;
      console.log('📥 Resposta da API:', response);
      console.log('📋 Registros:', response.registros);
      const dadosProcessados = (response.registros || []).map(item => ({
        id: item.Id,
        nomeOperacao: item.NomeOperacao || '',
        tipoDaOperacao: item.TipoDaOperacao || '',
        situacao: item.Situacao || '',
        diasRecorrencia: item.DiasRecorrencia || null,
        recorrenciaLigada: item.RecorrenciaLigada || false,
        gerarCobranca: item.GerarCobranca ? 'Sim' : 'Não',
        dataCriacao: item.DataCriacao ? new Date(item.DataCriacao).toLocaleDateString('pt-BR') : '',
        dataAtualizacao: item.DataModificacao ? new Date(item.DataModificacao).toLocaleDateString('pt-BR') : ''
      }));
      console.log('✅ Dados processados:', dadosProcessados);
      setData(dadosProcessados);
      setTotalRegistros(response.totalRegistros || dadosProcessados.length);
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('❌ Erro ao buscar dados:', error); // Erro tratado automaticamente pelo interceptor global do axios
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setFilterLoading(false);
        inFlightSignatureRef.current = null;
      }
    }
  }, [paginationState.pageIndex, paginationState.pageSize]); // Disparar fetch ao alterar paginação pelo setPaginationState

  useEffect(() => {
    if (!didInitialLoadRef.current) return;
    fetchOperacoes(filters); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationState.pageIndex, paginationState.pageSize]); // Carregar dados iniciais

  useEffect(() => {
    if (didInitialLoadRef.current) return; // evita dupla chamada em StrictMode

    didInitialLoadRef.current = true;
    setLoading(true);
    fetchOperacoes();
  }, []); // Função para aplicar filtros

  const handleFilterApply = useCallback(newFilters => {
    console.log('🔍 Aplicando filtros:', newFilters);
    setFilters(newFilters);
    setFilterLoading(true);
    fetchOperacoes(newFilters);
  }, [fetchOperacoes]); // Função para limpar filtros

  const handleLimparFiltros = useCallback(clearedFilters => {
    console.log('🧹 Limpar filtros clicado. Filtros limpos:', clearedFilters);
    setFilters(clearedFilters);
    setLoading(true);
    fetchOperacoes(clearedFilters);
  }, [fetchOperacoes]); // Paginação: garantir skeleton imediato ao alterar página/tamanho

  const handlePaginationChange = useCallback(next => {
    setLoading(true);
    setPaginationState(prev => {
      if (typeof next === 'function') return next(prev);
      if (typeof next === 'number') return { ...prev,
        pageIndex: next
      };
      if (next && typeof next === 'object') return { ...prev,
        ...next
      };
      return prev;
    });
  }, []); // Função para excluir operação

  const handleExcluir = useCallback(async id => {
    console.log('🗑️ Excluindo operação:', id);

    try {
      await deleteOperacao(id);
      notification.success('Operação excluída com sucesso!');
      fetchOperacoes(); // Recarregar dados
    } catch (error) {
      console.error('❌ Erro ao excluir operação:', error); // Erro tratado automaticamente pelo interceptor global do axios
    }
  }, [fetchOperacoes]);
  return {
    // Estado
    data,
    loading,
    filterLoading,
    totalRegistros,
    filters,
    paginationState,
    // Handlers
    handleFilterApply,
    handleLimparFiltros,
    handleExcluir,
    handlePaginationChange,
    setPaginationState,
    // Funções de carregamento
    fetchOperacoes
  };
};
